import { Directive, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatest,
  switchMap,
  map,
  interval,
  takeWhile,
  endWith,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs';
import { Destroy } from './destory';

/**
 * Quadratic Ease-Out Function: f(x) = x * (2 - x)
 */
const easeOutQuad = (x: number): number => x * (2 - x);

@Directive({
  selector: '[countUp]',
  standalone: true,
  providers: [Destroy],
})
export class CountUpDirective implements OnInit {

  @Input('countUp')
  set count(count: number | string) {
    const num = Number(count);
    if (!Number.isNaN(num)) {
      this.count$.next(num);
    } else {
      console.log("Not a valid number");
    }
  }
  @Input()
  set duration(duration: number) {
    this.duration$.next(duration);
  }
  @Input() prefix: string = '';
  @Input() suffix: string = '';
  @Input() useComma: boolean = true;
  @Input() useKMB: boolean = true;

  private readonly count$ = new BehaviorSubject(0);
  private readonly duration$ = new BehaviorSubject(2000);

  constructor(
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly destroy$: Destroy
  ) { }

  ngOnInit(): void {
    this.displayCurrentCount();
  }

  private readonly currentCount$ = combineLatest([
    this.count$,
    this.duration$,
  ]).pipe(
    switchMap(([count, duration]) => {
      // get the time when animation is triggered
      const startTime = animationFrameScheduler.now();

      return interval(0, animationFrameScheduler).pipe(
        // calculate elapsed time
        map(() => animationFrameScheduler.now() - startTime),
        // calculate progress
        map((elapsedTime) => elapsedTime / duration),
        // complete when progress is greater than 1
        takeWhile((progress) => progress <= 1),
        // apply quadratic ease-out
        // for faster start and slower end of counting
        map(easeOutQuad),
        // calculate current count
        map((progress) => Math.round(progress * count)),
        // make sure that last emitted value is count
        endWith(count),
        distinctUntilChanged()
      );
    })
  );

  private displayCurrentCount(): void {
    this.currentCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((currentCount) => {
        let formatted = '';
        if (this.useKMB) {
          if (currentCount >= 1000000) {
            const formattedNum = (currentCount / 1000000).toFixed(1);
            formatted = (formattedNum.endsWith('.0') ? formattedNum.slice(0, -2) : formattedNum) + 'M';
          } else if (currentCount >= 1000) {
            const formattedNum = (currentCount / 1000).toFixed(1);
            formatted = (formattedNum.endsWith('.0') ? formattedNum.slice(0, -2) : formattedNum) + 'k';
          } else {
            formatted = currentCount.toString();
          }
        } else {
          formatted = this.useComma ? currentCount.toLocaleString('en-US') : currentCount.toString();
        }

        if (this.prefix) {
          formatted = this.prefix + formatted;
        }
        if (this.suffix) {
          formatted = formatted + this.suffix;
        }
        this.renderer.setProperty(
          this.elementRef.nativeElement,
          'innerHTML',
          formatted
        );
      });
  }
}
